import {storage, STORAGE_KEYS} from '@/utils/storage'
import {isNearAirport} from '@/utils/airports'
import {serverDirectionFor} from '@/utils/serverDirection'

/**
 * Build the /calculate-policies request body from the in-progress booking
 * (sessionStorage), mirroring the plugin's tripService.calculatePolicies.
 *
 * The endpoint takes ONLY trip_direction + distance_meters — it returns the
 * config-based policy BANDS, not a per-trip price, so scheduled_at / final_price
 * were never part of this request and are intentionally omitted.
 */
export function buildPolicyParams() {
    // trip_direction — destinations layout: [pickup, dropoff, ...stopovers]
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []) || []
    const pickup = destinations[0]
    const dropoff = destinations[1]
    const isPickupAirport = pickup?.lat && pickup?.lng ? !!isNearAirport(pickup.lat, pickup.lng) : false
    const isDropOffAirport = dropoff?.lat && dropoff?.lng ? !!isNearAirport(dropoff.lat, dropoff.lng) : false
    const trip_direction = serverDirectionFor(pickup, dropoff)?.direction
        ?? (isPickupAirport ? 'from_airport' : isDropOffAirport ? 'to_airport' : 'others')

    // distance_meters — route breakdown carries a top-level total distance in km
    const routeData = storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN)
    const distance_meters = Math.round((parseFloat(routeData?.distance_km) || 0) * 1000)

    return {trip_direction, distance_meters}
}

/**
 * True once the trip is defined enough to compute policies — a real trip_direction
 * (both endpoints placed) AND a calculated route. usePolicies gates on this so a
 * modal opening before the banner has a route can't cache an empty "others / 0"
 * response for the whole session (staleTime Infinity).
 *
 * NOTE: trip_direction is deliberately NOT checked — 'others' is a legitimate
 * non-airport direction, so it can't distinguish "no pickup yet" from a real
 * city-to-city trip. The raw destinations are the honest signal.
 */
export function hasPolicyParams(p) {
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []) || []
    return !!(
        destinations[0]?.lat && destinations[0]?.lng &&
        destinations[1]?.lat && destinations[1]?.lng &&
        p?.distance_meters
    )
}
